import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { splitLangPath, withLang } from "../i18n/urls";
import { DIVISIONS } from "../data/mock";

// Map division id → route
const ROUTE_MAP = {
  supercar: "/supercar-luxury",
  yuppirent: "/noleggio-low-cost",
  urban: "/urban-cars",
  corporate: "/corporate-fleet",
  yachting: "/yachts",
  aviation: "/aviation",
  realestate: "/real-estate",
  primecare: "/prime-care",
  technology: "/technology",
  wallet: "/credit-wallet",
  members: "/membership",
  longterm: "/long-term-de",
  cargo: "/cargo",
  kartoonia: "/kartoonia",
};

const Card = ({ d, t }) => {
  const { pathname } = useLocation();
  const lang = splitLangPath(pathname).lang;
  const to = withLang(ROUTE_MAP[d.id] || "/", lang);
  return (
    <Link
      id={d.id}
      to={to}
      data-testid={`division-card-${d.id}`}
      className={`group relative block overflow-hidden bg-[#0a0a0a] border border-white/5 hover:border-arabell-gold/40 transition-colors duration-500 ${d.span}`}
    >
      <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[1400ms] ease-out group-hover:scale-110"
          style={{ backgroundImage: `url(${d.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-700" />
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
          {d.logo && (
            <img
              src={d.logo}
              alt=""
              className="h-7 md:h-9 w-auto mb-4 opacity-95"
              data-testid={`division-logo-${d.id}`}
            />
          )}
          <h3 className="font-display text-lg md:text-2xl lg:text-3xl text-white tracking-wide mb-2">
            {t(`services.division_titles.${d.id}`, { defaultValue: d.title })}
          </h3>
          <p className="hidden md:block text-xs md:text-sm text-white/60 max-w-md leading-relaxed">
            {t(`services.divisions.${d.id}`, { defaultValue: d.description })}
          </p>
          <div className="mt-4 flex items-center gap-3 text-arabell-gold text-[10px] tracking-[0.4em] uppercase opacity-80 group-hover:opacity-100 transition-opacity">
            <span>
              {d.ctaKey
                ? t(d.ctaKey, { defaultValue: t("services.discover", { defaultValue: "Discover" }) })
                : t("services.discover", { defaultValue: "Discover" })}
            </span>
            <ArrowUpRight
              className="w-4 h-4 shrink-0 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              strokeWidth={1.5}
            />
          </div>
        </div>
      </div>
    </Link>
  );
};

const Divisions = () => {
  const { t } = useTranslation();
  return (
    <section className="relative bg-black py-16 md:py-24">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[10px] tracking-[0.5em] uppercase text-arabell-gold mb-4">
            {t("services.kicker")}
          </p>
          <h2 className="font-display text-3xl md:text-5xl text-white tracking-[0.1em] uppercase">
            {t("services.headline")}
          </h2>
          <div className="w-16 h-px bg-arabell-gold mx-auto mt-6" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {DIVISIONS.map((d) => (
            <Card key={d.id} d={d} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Divisions;
